import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import NeoButton from "../../../components/Common/NeoButton";
import NeoInput from "../../../components/Common/NeoInput";
import NeoCard from "../../../components/Common/NeoCard";
import NeoTable from "../../../components/Common/NeoTable";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import usePlatformMercadoPago from "./hooks/usePlatformMercadoPago";
import { PaymentStatusBadge } from "../components/TenantStatusBadge";

const PlatformMercadoPago = () => {
    const { sessions, loading, syncing, syncResult, error, loadSessions, syncPayment } = usePlatformMercadoPago();
    const [paymentId, setPaymentId] = useState("");

    useEffect(() => { loadSessions(); }, [loadSessions]);

    const handleSync = async (e) => {
        e.preventDefault();
        if (!paymentId.trim()) return;
        await syncPayment(paymentId.trim());
    };

    // Colunas da NeoTable
    const columns = [
        {
            key: "externalReference",
            label: "Referência",
            width: "15%",
            render: (row) => <span className="platform-mono">{row.externalReference || "—"}</span>
        },
        {
            key: "providerPaymentId",
            label: "Payment ID",
            width: "15%",
            render: (row) => <span className="platform-mono">{row.providerPaymentId || "—"}</span>
        },
        {
            key: "tenantName",
            label: "Academia",
            width: "25%",
            render: (row) => <span>{row.tenantName || "—"}</span>
        },
        {
            key: "amount",
            label: "Valor",
            width: "15%",
            render: (row) => (
                <span className="fw-semibold">
                    {row.amount ? `R$ ${row.amount.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—"}
                </span>
            )
        },
        {
            key: "paymentStatus",
            label: "Status",
            width: "12%",
            render: (row) => <PaymentStatusBadge status={row.paymentStatus} />
        },
        {
            key: "paymentMethodId",
            label: "Método",
            width: "10%",
            render: (row) => <span className="platform-text-sm">{row.paymentMethodId || "—"}</span>
        },
        {
            key: "createdAt",
            label: "Data",
            width: "8%",
            render: (row) => <span>{row.createdAt ? new Date(row.createdAt).toLocaleDateString("pt-BR") : "—"}</span>
        }
    ];

    return (
        <div className="pb-5">
            {/* Header com o título do módulo */}
            <div className="mb-4">
                <Breadcrumb title="Mercado Pago" />
            </div>

            <Row className="g-4 mb-4">
                <Col lg={12}>
                    <NeoCard
                        title="Sincronizar Pagamento por ID"
                        subtitle="Consulte e force a conciliação manual de transações pendentes buscando diretamente pelo ID do gateway."
                        headerIcon="mdi-swap-horizontal"
                        thinBorder={true}
                        bodyClassName="p-4"
                    >
                        <form onSubmit={handleSync} className="d-flex gap-3 align-items-end">
                            <div className="flex-grow-1">
                                <NeoInput
                                    label="Payment ID do Mercado Pago"
                                    placeholder="Ex: 12345678"
                                    value={paymentId}
                                    onChange={(e) => setPaymentId(e.target.value)}
                                    disabled={syncing}
                                />
                            </div>
                            <NeoButton
                                type="submit"
                                disabled={syncing || !paymentId.trim()}
                                loading={syncing}
                                loadingText="Sincronizando..."
                                icon="ph-light ph-arrows-clockwise"
                            >
                                Sincronizar
                            </NeoButton>
                        </form>

                        {syncResult && (
                            <div className="alert alert-success mt-3 platform-text-sm">
                                <strong>Pagamento #{syncResult.paymentId}</strong> — Status: <strong>{syncResult.status}</strong>
                                {syncResult.statusDetail && ` (${syncResult.statusDetail})`}
                                {syncResult.transactionAmount && ` — R$ ${syncResult.transactionAmount.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                                <br />
                                Session encontrada: {syncResult.sessionFound ? "Sim" : "Não"} |
                                Session atualizada: {syncResult.sessionUpdated ? "Sim" : "Não"}
                                {syncResult.tenantUpdated && ` | Tenant atualizado: ${syncResult.tenantUpdated}`}
                            </div>
                        )}

                        {error && <div className="alert alert-danger mt-3 platform-text-sm">{error}</div>}
                    </NeoCard>
                </Col>
            </Row>

            <NeoCard
                title="Últimos Checkouts com Pagamento"
                subtitle="Lista dos checkouts mais recentes iniciados por clientes com ID de pagamento registrado no Mercado Pago."
                headerIcon="mdi-credit-card-outline"
                thinBorder={true}
                bodyClassName="p-0"
            >
                <NeoTable
                    columns={columns}
                    data={sessions.filter(s => s.providerPaymentId)}
                    loading={loading}
                    emptyMessage="Nenhum pagamento processado encontrado."
                />
            </NeoCard>
        </div>
    );
};

export default PlatformMercadoPago;
